import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Divider, Paper, Stack, Typography } from '@mui/material';

import type { MediaImage, PropertyRoomTypeWithImages } from '../ImagesVideo';
import { ROOM_IMAGE_LIMIT } from '../utils';
import type { UploadedRoomAssignment } from '../utils';

import ImagesByRoomRow from './Row';
import { EMPTY_IMAGE_PUBLIC_IDS, buildRoomImagesByKey, getRoomKey, pruneUnavailableRoomImages } from './utils';
import type { RoomImagesByKey } from './utils';

interface Props {
  excludedImagePublicIds?: Array<string>;
  hiddenImagePublicIds?: Array<string>;
  mediaImages: Array<MediaImage>;
  onAssignImagesToRoom: (roomName: string, selectedImages: Array<MediaImage>) => void;
  onRemoveImageFromRoom: (publicId: string) => void;
  onToggleHiddenImage: (publicId: string) => void;
  propertyRoomTypes?: Array<PropertyRoomTypeWithImages>;
  uploadedRoomAssignments?: Array<UploadedRoomAssignment>;
}

function getRoomSourceSignature(propertyRoomTypes: Array<PropertyRoomTypeWithImages>) {
  return propertyRoomTypes
    .map((room, index) => {
      const imagePublicIds = (room.images ?? []).map((image) => image.id_cloudinary_external ?? '').join(',');

      return `${getRoomKey(room, index)}:${imagePublicIds}`;
    })
    .join('|');
}

export default function ImagesByRoom({
  excludedImagePublicIds = EMPTY_IMAGE_PUBLIC_IDS,
  hiddenImagePublicIds = EMPTY_IMAGE_PUBLIC_IDS,
  mediaImages,
  onAssignImagesToRoom,
  onRemoveImageFromRoom,
  onToggleHiddenImage,
  propertyRoomTypes = [],
  uploadedRoomAssignments = [],
}: Props) {
  const [roomImagesByKey, setRoomImagesByKey] = useState<RoomImagesByKey>(() =>
    pruneUnavailableRoomImages(buildRoomImagesByKey(propertyRoomTypes, excludedImagePublicIds), mediaImages),
  );
  const dismissedUploadedRoomAssignmentPublicIds = useRef<Set<string>>(new Set());
  const roomSourceSignature = useMemo(() => getRoomSourceSignature(propertyRoomTypes), [propertyRoomTypes]);
  const previousRoomSourceSignature = useRef(roomSourceSignature);

  const dismissUploadedRoomAssignments = useCallback((publicIds: Array<string>) => {
    if (publicIds.length === 0) {
      return;
    }

    dismissedUploadedRoomAssignmentPublicIds.current = new Set([
      ...dismissedUploadedRoomAssignmentPublicIds.current,
      ...publicIds,
    ]);
  }, []);

  useEffect(() => {
    const hasRoomSourceChanged = previousRoomSourceSignature.current !== roomSourceSignature;

    if (hasRoomSourceChanged) {
      previousRoomSourceSignature.current = roomSourceSignature;
      dismissedUploadedRoomAssignmentPublicIds.current = new Set();
      setRoomImagesByKey(
        pruneUnavailableRoomImages(buildRoomImagesByKey(propertyRoomTypes, excludedImagePublicIds), mediaImages),
      );
      return;
    }

    setRoomImagesByKey((currentRoomImagesByKey) => pruneUnavailableRoomImages(currentRoomImagesByKey, mediaImages));
  }, [excludedImagePublicIds, mediaImages, propertyRoomTypes, roomSourceSignature]);

  useEffect(() => {
    if (uploadedRoomAssignments.length === 0) {
      return;
    }

    const activeUploadedRoomAssignments = uploadedRoomAssignments.filter(
      (assignment) => !dismissedUploadedRoomAssignmentPublicIds.current.has(assignment.publicId),
    );

    if (activeUploadedRoomAssignments.length === 0) {
      return;
    }

    const mediaImagesByPublicId = new Map(mediaImages.map((image) => [image.publicId, image]));

    setRoomImagesByKey((currentRoomImagesByKey) => {
      let nextRoomImagesByKey = currentRoomImagesByKey;
      const processedPublicIds: Array<string> = [];

      activeUploadedRoomAssignments.forEach((assignment) => {
        const mediaImage = mediaImagesByPublicId.get(assignment.publicId);

        if (!mediaImage) {
          return;
        }

        processedPublicIds.push(assignment.publicId);

        const currentRoomImages = nextRoomImagesByKey[assignment.roomKey] ?? [];

        if (
          currentRoomImages.length >= ROOM_IMAGE_LIMIT ||
          currentRoomImages.some((image) => image.publicId === assignment.publicId)
        ) {
          return;
        }

        const selectedPublicIds = new Set([assignment.publicId]);
        const nextRoomImagesWithoutSelection = Object.fromEntries(
          Object.entries(nextRoomImagesByKey).map(([roomKey, roomImages]) => [
            roomKey,
            roomKey === assignment.roomKey
              ? roomImages
              : roomImages.filter((image) => !selectedPublicIds.has(image.publicId)),
          ]),
        );

        nextRoomImagesByKey = {
          ...nextRoomImagesWithoutSelection,
          [assignment.roomKey]: currentRoomImages.concat({
            publicId: mediaImage.publicId,
            title: mediaImage.title,
          }),
        };
      });

      dismissUploadedRoomAssignments(processedPublicIds);

      return nextRoomImagesByKey;
    });
  }, [dismissUploadedRoomAssignments, mediaImages, uploadedRoomAssignments]);

  const handleAssignImagesToRoom = (roomName: string, selectedImages: Array<MediaImage>) => {
    dismissUploadedRoomAssignments(selectedImages.map((image) => image.publicId));
    onAssignImagesToRoom(roomName, selectedImages);
  };

  const handleRemoveImageFromRoom = (publicId: string) => {
    if (uploadedRoomAssignments.some((assignment) => assignment.publicId === publicId)) {
      dismissUploadedRoomAssignments([publicId]);
    }

    onRemoveImageFromRoom(publicId);
  };

  if (propertyRoomTypes.length === 0) {
    return null;
  }

  return (
    <Paper variant="outlined" sx={{ mt: 4, overflow: 'hidden' }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={1}
        sx={{ px: 3, py: 2.5 }}
      >
        <Typography variant="h5">Images by room</Typography>
        <Typography variant="body2" color="text.secondary">
          Each row matches a room in{' '}
          <Box component="span" sx={{ fontWeight: 700 }}>
            Rooms
          </Box>
          . Up to {ROOM_IMAGE_LIMIT} carousel slots.
        </Typography>
      </Stack>
      <Divider />

      <Stack divider={<Divider />} sx={{ px: 3 }}>
        {propertyRoomTypes.map((room, index) => {
          const roomKey = getRoomKey(room, index);

          return (
            <ImagesByRoomRow
              key={roomKey}
              hiddenImagePublicIds={hiddenImagePublicIds}
              mediaImages={mediaImages}
              onAssignImagesToRoom={handleAssignImagesToRoom}
              onRemoveImageFromRoom={handleRemoveImageFromRoom}
              onToggleHiddenImage={onToggleHiddenImage}
              room={room}
              roomImages={roomImagesByKey[roomKey] ?? []}
              roomKey={roomKey}
              setRoomImagesByKey={setRoomImagesByKey}
            />
          );
        })}
      </Stack>
    </Paper>
  );
}
